import { Form, Input, Modal, theme, Typography } from 'antd';
import { useMutation } from 'jsonapi-react';
import { type ReactNode, useEffect, useMemo } from 'react';
import { catchError, EMPTY, from, of, switchMap, tap, throwError } from 'rxjs';
import PrepSheetFilterFields, { getExclusiveIdsInTree } from './prep-sheet-filter-fields.component';
import { PrepSheetConfiguration } from '../../../entities';
import { useNotification } from '../../../hooks/use-notification';
import { type DisplayConfig } from '../../../types';

const SUCCESS_SAVE_MESSAGE = 'Preparation sheet configuration updated successfully';
const FAILURE_SAVE_MESSAGE = 'Failed to update preparation sheet configuration';

interface Props {
  configuration: PrepSheetConfiguration;
  onSuccess: () => void;
  onCancel: () => void;
}

interface FiltersType {
  site?: string[];
  mealCategory?: string[];
  mainType?: string[];
}

interface FormFieldsType {
  name: string;
  filters: FiltersType;
  enabledDisplayConfig: Array<keyof DisplayConfig>;
}

const PrepSheetConfigurationFormModal = (props: Props): ReactNode => {
  const { configuration, onSuccess, onCancel } = props;
  const notification = useNotification();
  const { token } = theme.useToken();
  const [formInstance] = Form.useForm<FormFieldsType>();
  const enabledDisplayConfigWatch = Form.useWatch('enabledDisplayConfig', formInstance);
  const [mutatePrepSheetConfiguration, prepSheetConfigurationData] =
    useMutation<PrepSheetConfiguration>(['prep-sheet-configuration', configuration.id]);

  const initialValues = useMemo<FormFieldsType>(
    () => ({
      name: configuration.name,
      filters: {
        site: configuration.site ?? [],
        mealCategory: configuration.mealCategory ?? [],
        mainType: configuration.mainType ?? [],
      },
      enabledDisplayConfig: configuration.flags ?? ['showSummary'],
    }),
    [configuration]
  );

  useEffect(() => {
    formInstance.setFieldsValue(initialValues);
  }, [initialValues]);

  const onSubmit = (values: FormFieldsType): void => {
    const sitesIds = getExclusiveIdsInTree(values.filters.site ?? []);

    const prepSheetConfiguration = new PrepSheetConfiguration(
      values.name.trim(),
      values.enabledDisplayConfig?.length ? values.enabledDisplayConfig : ['showSummary'],
      sitesIds.length ? sitesIds : null,
      values.filters.mealCategory?.length ? values.filters.mealCategory : null,
      values.filters.mainType?.length ? values.filters.mainType : null
    );
    prepSheetConfiguration.id = configuration.id;

    from(mutatePrepSheetConfiguration(prepSheetConfiguration))
      .pipe(
        switchMap((res) => {
          if (res.error || res.errors) {
            return throwError(() => new Error(res.error?.detail ?? FAILURE_SAVE_MESSAGE));
          }
          return of(res.data);
        }),
        tap(() => {
          notification.success({
            message: ' ',
            description: SUCCESS_SAVE_MESSAGE,
          });
          onSuccess();
        }),
        catchError((error: Error) => {
          notification.error({
            message: ' ',
            description: error.message,
          });
          return EMPTY;
        })
      )
      .subscribe();
  };

  return (
    <Modal
      open
      title={
        <Typography.Title style={{ marginBlockStart: 0, marginBlockEnd: token.marginLG }} level={3}>
          Edit preparation sheet configuration
        </Typography.Title>
      }
      confirmLoading={prepSheetConfigurationData.isLoading}
      onOk={() => {
        formInstance.submit();
      }}
      okButtonProps={{
        disabled: enabledDisplayConfigWatch === undefined || enabledDisplayConfigWatch.length === 0,
      }}
      onCancel={onCancel}
      okText="Save"
      width={800}
      forceRender
    >
      <Form<FormFieldsType>
        form={formInstance}
        layout="vertical"
        colon={false}
        requiredMark={false}
        initialValues={initialValues}
        onFinish={onSubmit}
      >
        <Form.Item<FormFieldsType>
          name="name"
          label="Name"
          rules={[{ required: true, message: 'Please enter a name' }]}
        >
          <Input placeholder="Enter name" />
        </Form.Item>

        <PrepSheetFilterFields />
      </Form>
    </Modal>
  );
};

export default PrepSheetConfigurationFormModal;
